<template>
  <el-dialog
    :visible="true"
    :close-on-click-modal="false"
    :before-close="handleClose"
    width="600"
  >
    <div v-loading="loading">
      <el-form
        ref="priceForm"
        label-position="top"
        :model="priceList"
        :rules="rules"
      >
        <el-form-item
          prop="name"
          label="Árlista megnevezése"
        >
          <el-input
            v-model="priceList.name"
            :disabled="dialogType === 'view'"
          />
        </el-form-item>
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item
              prop="type"
              label="Hozzárendelés típusa"
            >
              <el-select
                v-model="priceList.type"
                style="width: 100%"
                :disabled="dialogType === 'view'"
              >
                <el-option
                  value="partner"
                  label="Partner"
                >
                  Partner
                </el-option>
                <el-option
                  value="shop"
                  label="Üzlet"
                >
                  Üzlet
                </el-option>
                <el-option
                  value="group"
                  label="Csoport"
                >
                  Csoport
                </el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="16">
            <el-form-item
              v-if="priceList.type === 'partner'"
              prop="partner_id"
              label="Partner"
            >
              <el-select
                v-model="priceList.partner_id"
                filterable
                remote
                :multiple="dialogType === 'add'"
                :remote-method="searchPartners"
                :loading="remoteLoading"
                style="width: 100%;"
                :disabled="dialogType === 'view'"
              >
                <el-option
                  v-for="partner in partners"
                  :key="partner.partner_id"
                  :label="'#' + partner.partner_id +' ' + partner.partner_name"
                  :value="partner.partner_id"
                >
                  <strong>#{{ partner.partner_id }}</strong> {{ partner.partner_name }}
                </el-option>
              </el-select>
            </el-form-item>
            <el-form-item
              v-if="priceList.type === 'group'"
              prop="customer_group_id"
              label="Csoport"
            >
              <el-select
                v-model="priceList.customer_group_id"
                filterable
                remote
                :multiple="dialogType === 'add'"
                :remote-method="searchCustomerGroups"
                :loading="remoteLoading"
                style="width: 100%;"
                :disabled="dialogType === 'view'"
              >
                <el-option
                  v-for="group in customerGroups"
                  :key="group.id"
                  :label="'#' + group.id +' ' + group.name"
                  :value="group.id"
                >
                  <strong>#{{ group.id }}</strong> {{ group.name }}
                </el-option>
              </el-select>
            </el-form-item>
            <el-form-item
              v-show="priceList.type === 'shop'"
              prop="shop_id"
              label="Üzlet"
            >
              <el-select
                v-model="priceList.shop_id"
                filterable
                remote
                :remote-method="fetchShops"
                :loading="remoteLoading"
                :multiple="dialogType === 'add'"
                style="width: 100%;"
                :disabled="dialogType === 'view'"
              >
                <el-option
                  v-for="shop in shops"
                  :key="shop.shop_id"
                  :value="shop.shop_id"
                  :label="`#${shop.shop_id} ${shop.shop_name}`"
                >
                  <strong>#{{ shop.shop_id }}</strong> {{ shop.shop_name }}
                </el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item
              prop="valid_from"
              label="Érvényesség kezdete"
            >
              <el-date-picker
                v-model="priceList.from"
                type="date"
                style="width: 100%;"
                :clearable="true"
                format="yyyy. MM. dd."
                :disabled="dialogType === 'view'"
                value-format="yyyy-MM-dd"
                :picker-options="{ firstDayOfWeek: 1 }"
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item
              prop="valid_to"
              label="Érvényesség vége"
            >
              <el-date-picker
                v-model="priceList.to"
                type="date"
                style="width: 100%;"
                :clearable="true"
                :disabled="dialogType === 'view'"
                format="yyyy. MM. dd."
                value-format="yyyy-MM-dd"
                :picker-options="{ firstDayOfWeek: 1 }"
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-switch
              v-model="priceList.is_active"
              active-text="Aktív"
              inactive-text="Inaktív"
              :disabled="dialogType === 'view'"
              style="margin-top: 32px;"
            />
          </el-col>
        </el-row>
        <p-table
          ref="productItems"
          :key="tableKey"
          :data="priceList.items"
          :empty-line="{
            product_id: null,
            commission_price_type_id: null,
            price: 0,
          }"
          dialog="RepresentativeSpecialPriceDialog"
          :opts="tableOptions"
          :columns="[
            {
              label: 'Termék ID',
              prop: 'product_id',
            },
            {
              label: 'Termék',
              prop: 'product_name',
              minWidth: 130,
              showTooltip: true
            },
            {
              label: 'Ár',
              prop: 'price',
              align: 'right',
              formatter: formatPrice
            },
            {
              label: 'Jutalék számítás',
              prop: 'commission_price_type_id',
              formatter: (priceTypes, id) => priceTypes && priceTypes.find(pt => pt.id === id) && priceTypes.find(pt => pt.id === id).name || '',
              minWidth: 90
            }
          ]"
        />
      </el-form>
    </div>
    <div slot="title">
      {{ title }}
    </div>
    <div
      v-if="dialogType !== 'view'"
      slot="footer"
      class="dialog-footer"
    >
      <el-button
        type="primary"
        @click="store"
        :loading="buttonLoading"
      >
        {{ buttonText }}
      </el-button>
    </div>
  </el-dialog>
</template>
<script>
  import PTable from '../../utils/PTable';
  import numberFormatter from '../../../services/NumberFormatter'

  export default {
    components: { PTable },
    props: ['dialogType', 'listId', 'copyFrom', 'addType'],
    data() {
      return {
        tableKey: 1,
        remoteLoading: false,
        productSelectLoading: false,
        loading: false,
        buttonLoading: false,
        partners: [],
        customerGroups: [],
        shops: [],
        products: [],
        idsToDelete: [],
        priceList: {
          partner_id: null,
          shop_id: null,
          items: null,
          is_active: true,
        },
        prices: {
          type: '',
          partner: null,
          shop: null,
          valid_from: null,
          valid_to: null,
          products: [],
        },
        rules: {
          type: [
            {
              required: true,
              message: 'Mező megadása kötelező',
              trigger: 'blur'
            }
          ],
          name: [
            {
              required: true,
              message: 'Mező megadása kötelező',
              trigger: 'change'
            }
          ],
          from: [
            {
              required: true,
              message: 'Mező megadása kötelező',
              trigger: 'change'
            }
          ],
          to: [
            {
              required: true,
              message: 'Mező megadása kötelező',
              trigger: 'change'
            }
          ],
        },
      };
    },
    computed: {
      tableOptions() {
        return {
          currency: 'HUF',
          addItem: this.dialogType !== 'view',
          updateItem: this.dialogType !== 'view',
          deleteItem: this.dialogType !== 'view',
          editableFields: [],
        };
      },
      title() {
        if (this.dialogType === 'add') {
          return 'Új egyedi árlista';
        } else if (this.dialogType === 'update') {
          return 'Egyedi árlista módosítása';
        } else if (this.dialogType === 'view') {
          return 'Egyedi árlista megtekintése';
        } else {
          return '';
        }
      },
      buttonText() {
        if (this.dialogType === 'add') {
          return 'Hozzáadása';
        } else if (this.dialogType === 'update') {
          return 'Módosítás';
        } else {
          return '';
        }
      },

      priceTypes() {
        return this.$store.getters['priceType/priceTypes'];
      }
    },

    async mounted() {
      this.loading = true;
      if (!this.priceTypes.length) {
        await this.$store.dispatch('priceType/fetchAll');
      }

      if (this.listId || this.copyFrom) {

        this.priceList = await axios.get('api/representative-price-lists/' + (this.listId || this.copyFrom));
        if (this.priceList.partner_id) {
          this.partners = [{
            partner_id: this.priceList.partner_id,
            partner_name: this.priceList.partner_name,
          }];
        }
        if (this.priceList.shop_id) {
          this.shops = [{
            shop_id: this.priceList.shop_id,
            shop_name: this.priceList.shop_name,
          }];
        }
        if (this.priceList.customer_group_id) {
          this.customerGroups = [{
            id: this.priceList.customer_group_id,
            name: this.priceList.customer_group_name,
          }];
        }
        if (this.copyFrom) {
          if (this.priceList.partner_id) {
            this.priceList.partner_id = [this.priceList.partner_id];
          }
          if (this.priceList.customer_group_id) {
            this.priceList.customer_group_id = [this.priceList.customer_group_id];
          }
          if (this.priceList.shop_id) {
            this.priceList.shop_id = [this.priceList.shop_id];
          }
        }
        this.products = this.priceList.items.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
        }));
      }
      this.tableKey++;
      this.loading = false;

    },
    methods: {
      formatPrice (_, price) {
        return numberFormatter.localize(price)
      },
      isProductSelected(id) {
        return !!this.priceList.items.find(item => item.product_id === id);
      },
      removeItem(id) {
        this.priceList.items = this.priceList.items.filter(item => item.id !== id && item.key !== id);
      },
      checkProductValue(item) {
      },
      async fetchProducts(query) {
        if (query !== '') {
          this.productSelectLoading = true;
          this.products = (await axios.get('/api/products/name/' + query))
          .map(product => ({
            'product_id': product.id,
            'product_name': product.name,
          }));
          this.productSelectLoading = false;
        }
      },
      async searchPartners(query) {
        this.remoteLoading = true;
        if (!query) {
          this.partners = [];
        } else {
          const queryString = new URLSearchParams({
            name: query,
          });
          this.partners = (await this.$store.dispatch('partner/search', '?' + queryString.toString())).map(partner => ({
            partner_id: partner.id,
            partner_name: partner.company.name,
          }));
        }
        this.remoteLoading = false;
      },
      async searchCustomerGroups(query) {
        this.remoteLoading = true;
        if (!query) {
          this.customerGroups = [];
        } else {
          const queryString = new URLSearchParams({
            name: query,
          });
          this.customerGroups = (await axios.get(`api/search/customer-groups?${queryString.toString()}`));
        }
        this.remoteLoading = false;
      },

      async fetchShops(query) {
        if (query !== '') {
          this.remoteLoading = true;
          this.shops = (await axios.get('/api/shops/name/' + query)).map(shop => ({
            shop_id: shop.id,
            shop_name: shop.name,
          }));
          this.remoteLoading = false;
        }
      },

      handleClose() {
        if (this.dialogType === 'view') {
          return this.$router.replace('/representative-processes/representative-prices/custom');
        }
        this.$confirm('Biztosan elveted a módosításokat?', {
          confirmButtonText: 'Igen',
          type: 'warning',
        })
        .then(() => {
          this.$router.replace('/representative-processes/representative-prices/custom');
        })
        .catch(() => {
        });
      },

      addItem() {
        this.priceList.items.push({
          key: Math.random().toString(36).substring(7),
          product_id: null,
          price: 0,
        });
      },

      deleteProduct(index) {
        if (this.prices.products[index] && !this.prices.products[index].nowAdded) {
          this.idsToDelete = this.idsToDelete.concat(this.prices.products[index].priceTypes.filter(pt => {
            if (pt.id) {
              return pt.id;
            }
          })
          .map(pt => pt.id));
        }
        this.prices.products.splice(index, 1);
      },

      async store() {
        this.priceList.items = this.$refs['productItems'].getItems();
        let allProductPricesValid = true;
        this.priceList.items.forEach(item => {
          if (!item.product_id || !item.price) {
            return allProductPricesValid = false;
          }
        });
        if (!allProductPricesValid) {
          return this.$messageWrapper.error('Mezők nem megfelelően vannak kitöltve');
        }
        this.$refs['priceForm'].validate(async valid => {
          if (valid) {
            this.buttonLoading = true;
            try {
              if (this.dialogType === 'update') {
                await this.$store.dispatch('customRepresentativePriceList/update', this.priceList);
                this.$store.dispatch('customRepresentativePriceList/fetchAll');
              } else {
                await this.$store.dispatch('customRepresentativePriceList/create', this.priceList);
                this.$store.dispatch('customRepresentativePriceList/fetchAll');
              }
              this.$router.replace('/representative-processes/representative-prices/custom');
            } catch (e) {
            }
            this.buttonLoading = false;
          }
        });

      }
    }
  };
</script>
<style
  lang="scss">

  $borderColor: #EBEEF5;
  $headerColor: #909399;

  .price-type-table {
    border-left: 1px solid $borderColor;
    border-top: 1px solid $borderColor;
    width: 100%;

    td {
      border-right: 1px solid $borderColor;
      border-bottom: 1px solid $borderColor;
      padding: 12px 10px;
      margin-bottom: -1px;
      vertical-align: top;
    }

    .price-type-table__header {
      color: $headerColor;
      font-size: 14px;
      font-weight: bold;
    }
  }

  .add-item {
    border-bottom: 1px dotted #dadfe7;
    border-left: 1px dotted #dadfe7;
    border-right: 1px dotted #dadfe7;
    text-align: center;
    padding: 10px;
  }
</style>
